import { ApiError } from "@/lib/auth-api";
import {
	CONFLICT_MESSAGES,
	clientUnavailableReason,
	describeCause,
	fixAgentBlocker,
	jobsSummary,
	localDefaultOptions,
	printerFixable,
	printerNameError,
	reinstallBlocker,
	removeBlocker,
	removeReinstallNote,
	repairBlocker,
	spoolerResetDescription,
	versionBelow,
} from "./printer-labels";
import {
	type AddPrinterInput,
	addNetworkPrinter,
	disconnectPrinterConnection,
	type FixPrinterInput,
	fixPrinter,
	type NetworkInfo,
	type OperationKind,
	type OperationStarted,
	type PharmacyPrintersResponse,
	type PrinterDevice,
	type PrinterItem,
	type ReinstallPrinterInput,
	type RemoveConnectionInput,
	type RemovePrinterInput,
	reinstallPrinter,
	removePrinter,
	removePrinterConnection,
	repairPrinterConnection,
	resetSpooler,
	type SetDefaultInput,
	type ShareInput,
	setDefaultPrinter,
	startPrinterShare,
	unsharePrinter,
} from "./printers-api";

/**
 * Plan zmian drukarek: akcje z mapy i okien NIE startują od razu — dopisują
 * pozycję do szkicu, a „Wykonaj” uruchamia je po kolei istniejącymi
 * endpointami (backend przyjmuje jedną operację naraz w aptece). Akcja to 1:1
 * ciało jednego endpointu z `printers-api.ts`; nic tu nie zgaduje parametrów,
 * których nie wysłałby dzisiejszy panel.
 */
export type PlanAction =
	| { kind: "share"; input: ShareInput }
	| { kind: "disconnect"; connectionId: number; forget: boolean }
	| { kind: "repair"; connectionId: number }
	| { kind: "unshare"; shareId: number }
	| { kind: "remove_printer"; input: RemovePrinterInput }
	| { kind: "remove_connection"; input: RemoveConnectionInput }
	| { kind: "add_printer"; input: AddPrinterInput }
	| { kind: "fix_printer"; input: FixPrinterInput }
	| { kind: "reset_spooler"; deviceUuid: string }
	| { kind: "reinstall_printer"; input: ReinstallPrinterInput }
	| { kind: "set_default"; input: SetDefaultInput };

export type PlanActionKind = PlanAction["kind"];

/**
 * Nazwy z chwili dopisania — pozycja da się opisać także wtedy, gdy komputer
 * albo drukarka zniknęły już z inwentarza (wtedy ocena mówi „nie da się”).
 */
export type PlanNames = {
	/** Komputer, którego dotyczy akcja (kolejka, bufor, domyślna). */
	device?: string;
	/** Komputer z drukarką (udział). */
	host?: string;
	/** Stanowisko docelowe (połączenie). */
	client?: string;
	printer?: string;
	/** Np. adres drukarki sieciowej, nowa domyślna, sterownik. */
	extra?: string;
};

export type PlanItemStatus = "draft" | "running" | "succeeded" | "failed";

export type PlanItemError = { title: string; hint: string };

export type PlanItem = {
	id: string;
	action: PlanAction;
	names: PlanNames;
	addedAt: string;
	status: PlanItemStatus;
	/** Operacja backendu po starcie (202) — po przeładowaniu panel śledzi ją dalej. */
	operationId: number | null;
	/**
	 * Stanu operacji `operationId` nie dało się odczytać — mogła się wykonać.
	 * „Wznów” najpierw sprawdza ją ponownie, zamiast wysyłać drugi raz.
	 */
	outcomeUnknown?: boolean;
	error: PlanItemError | null;
	startedAt: string | null;
	finishedAt: string | null;
};

export type PlanAssessment = {
	/**
	 * `ready` — dane panelu nie widzą przeszkody; `check` — część warunków zna
	 * dopiero komputer albo backend (sprawdzi się przy wysyłce); `blocked` —
	 * nie da się wykonać, pozycja zostanie pominięta.
	 */
	state: "ready" | "check" | "blocked";
	reason: string;
};

/** Rodzaj operacji backendu, którą uruchomi akcja (do etykiet i grupowania kroków). */
export function planOperationKind(action: PlanAction): OperationKind {
	switch (action.kind) {
		case "share":
			return "share_connect";
		case "disconnect":
			return action.forget ? "forget" : "disconnect";
		default:
			return action.kind;
	}
}

/** Komputery, których dotyczy akcja (podświetlenie na mapie, czekanie na inwentarz). */
export function planActionDevices(action: PlanAction, view?: PharmacyPrintersResponse): string[] {
	switch (action.kind) {
		case "share":
			return [action.input.hostDeviceUuid, action.input.clientDeviceUuid];
		case "disconnect":
		case "repair": {
			const found = view ? findManagedConnection(view, action.connectionId) : null;
			return found ? [found.client.deviceUuid] : [];
		}
		case "unshare": {
			const found = view ? findManagedShare(view, action.shareId) : null;
			return found ? [found.host.deviceUuid] : [];
		}
		case "reset_spooler":
			return [action.deviceUuid];
		default:
			return [action.input.deviceUuid];
	}
}

const quote = (name: string | undefined) => `„${name ?? "drukarka"}”`;

/** Opis pozycji planu: tytuł (co się stanie) i jedno zdanie skutku. */
export function planItemText(item: Pick<PlanItem, "action" | "names">): {
	title: string;
	detail: string;
} {
	const { action, names } = item;
	const p = quote(names.printer);
	switch (action.kind) {
		case "share":
			return {
				title: `Udostępnij ${p} także na ${names.client ?? "stanowisko"}${action.input.setDefault ? " jako domyślną" : ""}`,
				detail: `Drukarka zostaje przy ${names.host ?? "komputerze z drukarką"} — ${names.client ?? "stanowisko"} dostaje do niej połączenie przez sieć apteki.`,
			};
		case "disconnect":
			return action.forget
				? {
						title: `Zapomnij połączenie ${names.client ?? "stanowiska"} z ${p}`,
						detail:
							"Tylko w panelu — komputer offline zachowa drukarkę, dopóki ktoś jej nie usunie.",
					}
				: {
						title: `Odłącz ${p} od ${names.client ?? "stanowiska"}`,
						detail: `Drukarka zostaje przy ${names.host ?? "komputerze z drukarką"}; poprzednia domyślna wraca.`,
					};
		case "repair":
			return {
				title: `Napraw połączenie ${names.client ?? "stanowiska"} z ${p}`,
				detail: "Przepina połączenie na bieżący adres komputera z drukarką i usuwa stare.",
			};
		case "unshare":
			return {
				title: `Cofnij udostępnienie ${p} na ${names.host ?? "komputerze"}`,
				detail: "Drukarka zostaje przy komputerze, inne stanowiska nie będą mogły jej dodać.",
			};
		case "remove_printer":
			return {
				title: `Usuń drukarkę ${p} z ${names.device ?? "komputera"}`,
				detail:
					action.input.newDefault != null
						? `Nowa domyślna: ${quote(names.extra ?? action.input.newDefault)}.`
						: "Kolejka zniknie z Windows.",
			};
		case "remove_connection":
			return {
				title: `Usuń połączenie ${p} z ${names.device ?? "komputera"}`,
				detail: `Konto Windows ${action.input.userName}${action.input.newDefault != null ? ` · nowa domyślna: ${quote(names.extra ?? action.input.newDefault)}` : ""}.`,
			};
		case "add_printer":
			return {
				title: `Dodaj drukarkę sieciową ${quote(action.input.printerName)} na ${names.device ?? "komputerze"}${action.input.setDefault ? " jako domyślną" : ""}`,
				detail: `${names.extra ?? action.input.host} · sterownik ${action.input.driverName}`,
			};
		case "fix_printer": {
			const what = [
				action.input.resume ? "Wznów" : null,
				action.input.purge ? "wyczyść kolejkę" : null,
			]
				.filter(Boolean)
				.join(" i ");
			return {
				title: `${what.charAt(0).toUpperCase()}${what.slice(1)} ${p} na ${names.device ?? "komputerze"}`,
				detail: action.input.purge
					? "Oczekujące zadania tej drukarki zostaną usunięte."
					: "Kolejka wróci do drukowania.",
			};
		}
		case "reset_spooler":
			return {
				title: `Zresetuj bufor wydruku na ${names.device ?? "komputerze"}`,
				detail:
					"Usługa Bufor wydruku startuje od nowa; zginą zawieszone zadania wszystkich drukarek.",
			};
		case "reinstall_printer":
			return {
				title: `Zainstaluj ponownie ${p} na ${names.device ?? "komputerze"}`,
				detail: `Kolejka TCP/IP od nowa, sterownik ${action.input.driverName}.`,
			};
		case "set_default":
			return {
				title: `Ustaw ${p} jako domyślną na ${names.device ?? "komputerze"}`,
				detail: "Dla zalogowanego użytkownika Windows.",
			};
	}
}

/**
 * Akcje, które coś usuwają albo odcinają drukowanie — przed wykonaniem planu
 * wymagają osobnego potwierdzenia (jak dziś każde z nich).
 */
export function isDestructive(action: PlanAction): boolean {
	switch (action.kind) {
		case "fix_printer":
			return action.input.purge;
		case "reset_spooler":
		case "remove_printer":
		case "remove_connection":
		case "disconnect":
		case "unshare":
		case "reinstall_printer":
			return true;
		default:
			return false;
	}
}

/** Skutek akcji destrukcyjnej do okna potwierdzenia — z bieżącego stanu komputera, gdy jest. */
export function destructiveConsequence(
	item: Pick<PlanItem, "action" | "names">,
	view: PharmacyPrintersResponse | undefined,
): string {
	const { action, names } = item;
	const device = (uuid: string) => view?.devices.find((d) => d.deviceUuid === uuid);
	switch (action.kind) {
		case "fix_printer": {
			const printer = device(action.input.deviceUuid)?.printers.find(
				(p) => p.name === action.input.printerName,
			);
			return `Wyczyszczenie kolejki ${quote(names.printer)} na ${names.device ?? "komputerze"}: ${printer ? jobsSummary(printer.jobs) : "liczba zadań nieznana."} Te wydruki zginą.`;
		}
		case "reset_spooler": {
			const d = device(action.deviceUuid);
			return d
				? spoolerResetDescription(d)
				: `Bufor wydruku na ${names.device ?? "komputerze"} startuje od nowa — zginą wszystkie oczekujące wydruki.`;
		}
		case "remove_printer": {
			const printer = device(action.input.deviceUuid)?.printers.find(
				(p) => p.name === action.input.printerName,
			);
			return `${quote(names.printer)} zniknie z ${names.device ?? "komputera"}. ${removeReinstallNote(printer ? printer.portKind : null).text}`;
		}
		case "remove_connection":
			return `Połączenie ${quote(names.printer)} zniknie z konta ${action.input.userName} na ${names.device ?? "komputerze"}. ${removeReinstallNote(null).text}`;
		case "disconnect":
			return action.forget
				? `${names.client ?? "Stanowisko"} jest offline albo ma starszego agenta — połączenie zniknie tylko z panelu, drukarka zostanie na tym komputerze.`
				: `${names.client ?? "Stanowisko"} przestanie drukować na ${quote(names.printer)}${names.host ? ` z ${names.host}` : ""}. Jeśli była domyślna, wróci poprzednia.`;
		case "unshare":
			return `${quote(names.printer)} przestanie być udostępniana z ${names.host ?? "komputera"}. Gdy to ostatni udział Farmastera na tym komputerze, znikną też reguły zapory.`;
		case "reinstall_printer":
			return `Kolejka ${quote(names.printer)} na ${names.device ?? "komputerze"} zostanie usunięta i założona od nowa — oczekujące wydruki zginą.`;
		default:
			return planItemText(item).detail;
	}
}

/** Klucz akcji — ta sama zmiana dwa razy w planie to pomyłka, nie zamiar. */
export function planActionKey(action: PlanAction): string {
	switch (action.kind) {
		case "share":
			return `share:${action.input.hostDeviceUuid}:${action.input.printerName}:${action.input.clientDeviceUuid}`;
		case "disconnect":
		case "repair":
			return `${action.kind}:${action.connectionId}`;
		case "unshare":
			return `unshare:${action.shareId}`;
		case "reset_spooler":
			return `reset_spooler:${action.deviceUuid}`;
		case "remove_printer":
			return `remove_printer:${action.input.deviceUuid}:${action.input.printerName}`;
		case "remove_connection":
			return `remove_connection:${action.input.deviceUuid}:${action.input.path}:${action.input.userName}`;
		case "add_printer":
			return `add_printer:${action.input.deviceUuid}:${action.input.printerName.toLowerCase()}`;
		case "fix_printer":
			return `fix_printer:${action.input.deviceUuid}:${action.input.printerName}:${action.input.purge}:${action.input.resume}`;
		case "reinstall_printer":
			return `reinstall_printer:${action.input.deviceUuid}:${action.input.printerName}`;
		case "set_default":
			return `set_default:${action.input.deviceUuid}:${action.input.printerName}`;
	}
}

function findManagedConnection(view: PharmacyPrintersResponse, connectionId: number) {
	for (const client of view.devices) {
		const connection = client.connections.find((c) => c.connectionId === connectionId);
		if (connection) return { client, connection };
	}
	return null;
}

function findManagedShare(view: PharmacyPrintersResponse, shareId: number) {
	for (const host of view.devices) {
		const printer = host.printers.find((p) => p.managedShare?.shareId === shareId);
		if (printer) return { host, printer };
	}
	return null;
}

/** Kontekst oceny: widok miejsca, sieci komputerów (gdy znane) i wcześniejsze pozycje planu. */
export type AssessContext = {
	view: PharmacyPrintersResponse;
	/** Udostępnianie istnieje tylko w aptece, nie w grupie urządzeń bez apteki. */
	sharing: boolean;
	/** Sieć komputera z `network-printers`; brak klucza = nieznana. */
	networks?: ReadonlyMap<string, NetworkInfo | null>;
	/** Pozycje przed ocenianą (bez niewykonalnych) — ich skutek panel uwzględnia. */
	earlier: readonly PlanItem[];
};

const ready = (reason: string): PlanAssessment => ({ state: "ready", reason });
const check = (reason: string): PlanAssessment => ({ state: "check", reason });
const blocked = (reason: string): PlanAssessment => ({ state: "blocked", reason });

/** Ostatni człon ścieżki `\\komputer\udział` (bez wielkości liter). */
function shareOfPath(path: string): string {
	return (path.split("\\").filter(Boolean).pop() ?? "").toLowerCase();
}

/** Wcześniejsza pozycja usuwa ten wpis („Odłącz”, „Usuń połączenie”, „Cofnij udostępnienie”). */
function earlierRemovesUsers(printer: PrinterItem, earlier: readonly PlanItem[]): boolean {
	const users = printer.usedBy;
	if (users.length === 0) return false;
	// Ręczne połączenie rozpoznajemy po udziale tej drukarki — bez nazwy udziału nie ma pewności.
	const share = printer.shareName?.toLowerCase() ?? null;
	return users.every((u) =>
		earlier.some((item) =>
			u.managed && u.connectionId != null
				? item.action.kind === "disconnect" && item.action.connectionId === u.connectionId
				: share != null &&
					item.action.kind === "remove_connection" &&
					item.action.input.deviceUuid === u.deviceUuid &&
					shareOfPath(item.action.input.path) === share &&
					(u.userName == null || item.action.input.userName === u.userName),
		),
	);
}

function earlierUnshares(printer: PrinterItem, earlier: readonly PlanItem[]): boolean {
	const shareId = printer.managedShare?.shareId;
	return (
		shareId != null &&
		earlier.some((item) => item.action.kind === "unshare" && item.action.shareId === shareId)
	);
}

function earlierRemovesPrinter(
	deviceUuid: string,
	printerName: string,
	earlier: readonly PlanItem[],
): boolean {
	return earlier.some(
		(item) =>
			item.action.kind === "remove_printer" &&
			item.action.input.deviceUuid === deviceUuid &&
			item.action.input.printerName === printerName,
	);
}

/** Blokada wynikająca z kolejki, którą wcześniejsze pozycje planu zdejmują. */
function printerBlockerResolved(
	printer: PrinterItem,
	reason: string,
	earlier: readonly PlanItem[],
): boolean {
	if (printer.managedShare && reason.startsWith("Najpierw cofnij")) {
		return earlierUnshares(printer, earlier);
	}
	if (printer.usedBy.length > 0 && reason.startsWith("Drukują na niej")) {
		return earlierRemovesUsers(printer, earlier);
	}
	return false;
}

function deviceOffline(device: PrinterDevice): string | null {
	if (!device.online) return `${device.displayName} jest offline`;
	return null;
}

function sameNetwork(
	a: string,
	b: string,
	networks: ReadonlyMap<string, NetworkInfo | null> | undefined,
): boolean | null {
	const na = networks?.get(a);
	const nb = networks?.get(b);
	if (!na || !nb) return null;
	return na.subnet === nb.subnet;
}

/**
 * Wstępna ocena pozycji z danych, które panel już ma (inwentarz, wersje
 * agentów, sieci), bez wywołań backendu. Ostatecznie decyduje backend przy
 * wysyłce — jego odmowa wraca jako błąd pozycji.
 */
export function assessPlanItem(item: PlanItem, ctx: AssessContext): PlanAssessment {
	const { view, earlier } = ctx;
	const key = planActionKey(item.action);
	if (earlier.some((e) => planActionKey(e.action) === key)) {
		return blocked("Ta sama zmiana jest już wyżej w planie.");
	}
	const byUuid = (uuid: string) => view.devices.find((d) => d.deviceUuid === uuid);
	const action = item.action;

	switch (action.kind) {
		case "share": {
			if (!ctx.sharing) return blocked("W grupie urządzeń bez apteki nie ma udostępniania.");
			const host = byUuid(action.input.hostDeviceUuid);
			const client = byUuid(action.input.clientDeviceUuid);
			if (!host) return blocked("Komputera z drukarką nie ma już w tym miejscu.");
			if (!client) return blocked("Stanowiska nie ma już w tym miejscu.");
			if (earlierRemovesPrinter(host.deviceUuid, action.input.printerName, earlier)) {
				return blocked("Wcześniejsza pozycja planu usuwa tę drukarkę.");
			}
			const printer = host.printers.find((p) => p.name === action.input.printerName);
			if (!printer || printer.port === "") {
				return blocked(`Drukarki nie ma już na ${host.displayName}.`);
			}
			if (!printer.cable) return blocked("Drukarka nie jest już podłączona kablem.");
			if (!host.online) return blocked(`Komputer z drukarką (${host.displayName}) jest offline.`);
			if (!host.supported) {
				return blocked(`${host.displayName} wymaga agenta ${view.minAgentVersion} lub nowszego.`);
			}
			const using = printer.usedBy.some((u) => u.deviceUuid === client.deviceUuid);
			const disconnecting =
				using &&
				printer.usedBy
					.filter((u) => u.deviceUuid === client.deviceUuid)
					.every((u) =>
						earlier.some(
							(e) => e.action.kind === "disconnect" && e.action.connectionId === u.connectionId,
						),
					);
			const reason = clientUnavailableReason(client, view.minAgentVersion, using && !disconnecting);
			if (reason) return blocked(`${client.displayName}: ${reason}.`);
			const net = sameNetwork(host.deviceUuid, client.deviceUuid, ctx.networks);
			if (net === false) {
				return blocked(
					`${client.displayName} jest w innej sieci niż ${host.displayName} — stanowisko nie dosięgnie drukarki.`,
				);
			}
			if (disconnecting) return check("Wcześniejsza pozycja odłącza obecne połączenie.");
			if (net == null) {
				return check(
					"Sieci komputerów panel nie zna — zasięg, zaporę i konto „serwis” sprawdzi wysyłka.",
				);
			}
			return ready(
				`Ta sama sieć · agent ${client.serviceVersion ?? "?"} · zaporę i konto „serwis” sprawdzi wysyłka.`,
			);
		}

		case "disconnect":
		case "repair": {
			const found = findManagedConnection(view, action.connectionId);
			if (!found) return blocked("Tego połączenia już nie ma.");
			const { client, connection } = found;
			if (action.kind === "repair") {
				const host = connection.hostDeviceUuid ? byUuid(connection.hostDeviceUuid) : undefined;
				const reason = repairBlocker(client, host, false);
				return reason ? blocked(reason) : ready("Oba komputery online.");
			}
			if (action.forget) {
				return client.online && client.supported
					? blocked(`${client.displayName} jest już online — użyj „Odłącz”.`)
					: ready("Tylko wpis w panelu — komputera nie dotykamy.");
			}
			const off = deviceOffline(client);
			if (off) return blocked(`${off} — można tylko zapomnieć połączenie.`);
			if (!client.supported) return blocked(`${client.displayName} ma za starego agenta.`);
			return check("Użytkownika Windows, u którego dodano drukarkę, sprawdzi wysyłka.");
		}

		case "unshare": {
			const found = findManagedShare(view, action.shareId);
			if (!found) return blocked("Tego udostępnienia już nie ma.");
			const { host, printer } = found;
			const off = deviceOffline(host);
			if (off) return blocked(off);
			if (!host.supported) return blocked(`${host.displayName} ma za starego agenta.`);
			const active = printer.usedBy.filter((u) => u.managed && u.connectionId != null);
			if (active.length > 0) {
				if (earlierRemovesUsers({ ...printer, usedBy: active }, earlier)) {
					return check("Wcześniejsze pozycje odłączają stanowiska, które z niej drukują.");
				}
				return blocked("Najpierw odłącz stanowiska, które z niej korzystają.");
			}
			return ready(`${host.displayName} online.`);
		}

		case "remove_printer": {
			const device = byUuid(action.input.deviceUuid);
			if (!device) return blocked("Komputera nie ma już w tym miejscu.");
			const printer = device.printers.find((p) => p.name === action.input.printerName);
			if (!printer || printer.port === "") return blocked("Tej drukarki nie ma już na komputerze.");
			const reason = removeBlocker(device, printer, false, view.removeMinAgentVersion);
			if (reason) {
				return printerBlockerResolved(printer, reason, earlier)
					? check("Wcześniejsze pozycje zdejmują to, co dziś blokuje usunięcie.")
					: blocked(reason);
			}
			return ready(`${device.displayName} online.`);
		}

		case "remove_connection": {
			const device = byUuid(action.input.deviceUuid);
			if (!device) return blocked("Komputera nie ma już w tym miejscu.");
			const connection = device.connections.find(
				(c) => c.path === action.input.path && c.userName === action.input.userName,
			);
			if (!connection) return blocked("Tego połączenia już nie ma.");
			const reason = removeBlocker(device, null, false, view.removeMinAgentVersion);
			if (reason) return blocked(reason);
			return check(`Zalogowanego użytkownika ${action.input.userName} sprawdzi wysyłka.`);
		}

		case "add_printer": {
			const device = byUuid(action.input.deviceUuid);
			if (!device) return blocked("Komputera nie ma już w tym miejscu.");
			const off = deviceOffline(device);
			if (off) return blocked(off);
			if (!device.addSupported) {
				return blocked(
					device.serviceVersion && versionBelow(device.serviceVersion, view.addMinAgentVersion)
						? `Wymaga agenta ${view.addMinAgentVersion} lub nowszego.`
						: "Brak danych o sieci komputera — odśwież.",
				);
			}
			const nameError = printerNameError(
				action.input.printerName,
				device.printers.filter((p) => p.port !== "").map((p) => p.name),
			);
			if (nameError) return blocked(nameError);
			return check("Czy komputer jest wciąż w sieci drukarki, sprawdzi wysyłka.");
		}

		case "fix_printer":
		case "reinstall_printer":
		case "set_default":
		case "reset_spooler": {
			const uuid = action.kind === "reset_spooler" ? action.deviceUuid : action.input.deviceUuid;
			const device = byUuid(uuid);
			if (!device) return blocked("Komputera nie ma już w tym miejscu.");
			const off = deviceOffline(device);
			if (off) return blocked(off);
			const agent = fixAgentBlocker(device, view.fixMinAgentVersion);
			if (agent) return blocked(`${agent}.`);
			if (action.kind === "reset_spooler") return ready(`${device.displayName} online.`);
			if (action.kind === "set_default") {
				const options = localDefaultOptions(device);
				const option = options.find((o) => o.value === action.input.printerName);
				if (earlierRemovesPrinter(device.deviceUuid, action.input.printerName, earlier)) {
					return blocked("Wcześniejsza pozycja planu usuwa tę drukarkę.");
				}
				if (!option) {
					const added = earlier.some(
						(e) =>
							e.action.kind === "add_printer" &&
							e.action.input.deviceUuid === device.deviceUuid &&
							e.action.input.printerName === action.input.printerName,
					);
					return added
						? check("Drukarkę dodaje wcześniejsza pozycja planu.")
						: blocked("Tej drukarki nie ma już na komputerze.");
				}
				if (option.isDefault) return blocked("Ta drukarka już jest domyślna.");
				return check("Jedynego zalogowanego użytkownika Windows sprawdzi wysyłka.");
			}
			if (earlierRemovesPrinter(device.deviceUuid, action.input.printerName, earlier)) {
				return blocked("Wcześniejsza pozycja planu usuwa tę drukarkę.");
			}
			const printer = device.printers.find((p) => p.name === action.input.printerName);
			if (!printer || !printerFixable(printer))
				return blocked("Tej kolejki nie ma już na komputerze.");
			if (action.kind === "reinstall_printer") {
				const reason = reinstallBlocker(device, printer);
				if (reason) {
					return printerBlockerResolved(printer, reason, earlier)
						? check("Wcześniejsze pozycje zdejmują to, co dziś blokuje reinstalację.")
						: blocked(reason);
				}
				return check("Czy komputer jest wciąż w sieci drukarki, sprawdzi wysyłka.");
			}
			return ready(jobsSummary(printer.jobs));
		}
	}
}

/** Start operacji pozycji istniejącym endpointem (202 `{operationId}`). */
export function startPlanAction(action: PlanAction): Promise<OperationStarted> {
	switch (action.kind) {
		case "share":
			return startPrinterShare(action.input);
		case "disconnect":
			return disconnectPrinterConnection(action.connectionId, action.forget);
		case "repair":
			return repairPrinterConnection(action.connectionId);
		case "unshare":
			return unsharePrinter(action.shareId);
		case "remove_printer":
			return removePrinter(action.input);
		case "remove_connection":
			return removePrinterConnection(action.input);
		case "add_printer":
			return addNetworkPrinter(action.input);
		case "fix_printer":
			return fixPrinter(action.input);
		case "reset_spooler":
			return resetSpooler(action.deviceUuid);
		case "reinstall_printer":
			return reinstallPrinter(action.input);
		case "set_default":
			return setDefaultPrinter(action.input);
	}
}

/** Odmowa startu (409 i inne) jako błąd pozycji — po polsku, z kodem 409 przez `CONFLICT_MESSAGES`. */
export function startErrorOf(error: unknown): PlanItemError {
	if (error instanceof ApiError) {
		if (error.code && CONFLICT_MESSAGES[error.code]) {
			return { title: "Backend odmówił startu", hint: CONFLICT_MESSAGES[error.code] };
		}
		if (error.status === 403) {
			return { title: "Brak dostępu", hint: "Brak dostępu do tej apteki, grupy lub urządzenia." };
		}
		return { title: "Backend odmówił startu", hint: error.message };
	}
	return {
		title: "Nie udało się wysłać",
		hint: error instanceof Error ? error.message : String(error),
	};
}

/** Porażka operacji (`cause`) jako błąd pozycji — te same komunikaty co postęp operacji. */
export function operationErrorOf(
	cause: string | null,
	errorDetail: string | null,
	kind: string,
): PlanItemError {
	return describeCause(cause, errorDetail, kind);
}

/**
 * Ocena całego planu po kolei: pozycja widzi skutek wcześniejszych (bez
 * niewykonalnych). Nieudane się liczą — „Wznów” ponawia je jako pierwsze.
 */
export function assessPlan(
	items: readonly PlanItem[],
	ctx: Omit<AssessContext, "earlier">,
): Map<string, PlanAssessment> {
	const result = new Map<string, PlanAssessment>();
	const earlier: PlanItem[] = [];
	for (const item of items) {
		const assessment = assessPlanItem(item, { ...ctx, earlier });
		result.set(item.id, assessment);
		if (assessment.state !== "blocked") earlier.push(item);
	}
	return result;
}

/** Operacje bez kontaktu z komputerem — po nich nie czekamy na świeży inwentarz. */
export function planActionTouchesDevices(action: PlanAction): boolean {
	return !(action.kind === "disconnect" && action.forget);
}
